import React, { useState, useEffect, useMemo } from 'react';
import {
  Mail,
  FileSpreadsheet,
  Calendar,
  FolderOpen,
  MessageSquare,
  UserCheck,
  Globe,
  CheckCircle,
  Loader2,
  AlertCircle,
  Send,
  Plus,
  ExternalLink,
  Users,
  RefreshCw,
  Inbox,
  Clock,
  Reply,
  Link2,
  Lock,
  ChevronRight,
  ShieldCheck,
  ArrowRight
} from 'lucide-react';
import { Lead } from '../types';
import { auth } from '../lib/firebase';
import { motion, AnimatePresence } from 'motion/react';
import Tooltip from './Tooltip';
import {
  sendGmailEmail,
  generateTrackingId,
  listRecentGmailMessages,
  fetchGmailThreadMessages,
  GmailMessageMeta,
  createGoogleSheet,
  appendToGoogleSheet,
  createCalendarEvent,
  listUpcomingCalendarEvents,
  CalendarEvent,
  listDriveFiles,
  listSpreadsheets,
  createGoogleContact,
  listGoogleContacts,
  GoogleContact,
  listChatSpaces,
  sendChatMessage,
  DriveFile,
  ChatSpace,
  isGoogleTokenExpired,
  notifyGoogleTokenExpired
} from '../lib/googleApi';
import { fetchTrackedEmails, saveTrackedEmail, TrackedEmail } from '../lib/db';

interface GoogleHubProps {
  accessToken: string | null;
  onConnectGoogle: () => void;
  onDisconnectGoogle: () => void;
  leads: Lead[];
  currentUserEmail: string | null;
  onLeadReply?: (leadId: string, reply: { from: string; date: string; snippet: string; trackingId: string }) => void;
  userSignature?: string;
}

const inputCls = "w-full bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 focus:border-blue-500 dark:focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-lg px-3.5 py-2 text-sm text-slate-800 dark:text-slate-100 outline-none transition-all shadow-inner placeholder:text-slate-400 dark:placeholder:text-slate-500 font-medium";
const labelCls = "block text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5";
const panelCls = "bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-sm transition-all";

export default function GoogleHub({
  accessToken,
  onConnectGoogle,
  onDisconnectGoogle,
  leads,
  currentUserEmail,
  onLeadReply,
  userSignature
}: GoogleHubProps) {
  // Service Tabs
  const [activeSubTab, setActiveSubTab] = useState<'gmail' | 'sheets' | 'calendar' | 'drive' | 'chat' | 'contacts'>('gmail');

  // Gmail State
  const [gmailLeadId, setGmailLeadId] = useState('');
  const [gmailTo, setGmailTo] = useState('');
  const [gmailSubject, setGmailSubject] = useState('');
  const [gmailBody, setGmailBody] = useState('');
  const [gmailLoading, setGmailLoading] = useState(false);
  const [gmailStatus, setGmailStatus] = useState<{ success: boolean; msg: string } | null>(null);

  // Tracked conversations + inbox
  const [trackedEmails, setTrackedEmails] = useState<TrackedEmail[]>([]);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [inboxMessages, setInboxMessages] = useState<GmailMessageMeta[]>([]);
  const [inboxLoading, setInboxLoading] = useState(false);

  // Sheets State
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetStatus, setSheetStatus] = useState<{ success: boolean; msg: string; url?: string } | null>(null);
  const [spreadsheets, setSpreadsheets] = useState<DriveFile[]>([]);
  const [spreadsheetsLoading, setSpreadsheetsLoading] = useState(false);

  // Calendar State
  const [calLeadId, setCalLeadId] = useState('');
  const [calSummary, setCalSummary] = useState('');
  const [calDescription, setCalDescription] = useState('Armellini CRM Logistics follow-up meeting');
  const [calDate, setCalDate] = useState('');
  const [calTime, setCalTime] = useState('10:00');
  const [calLoading, setCalLoading] = useState(false);
  const [calStatus, setCalStatus] = useState<{ success: boolean; msg: string } | null>(null);
  const [calEvents, setCalEvents] = useState<CalendarEvent[]>([]);
  const [calEventsLoading, setCalEventsLoading] = useState(false);

  // Drive/Picker State
  const [driveFiles, setDriveFiles] = useState<DriveFile[]>([]);
  const [driveLoading, setDriveLoading] = useState(false);
  const [driveError, setDriveError] = useState<string | null>(null);

  // Chat State
  const [chatSpaces, setChatSpaces] = useState<ChatSpace[]>([]);
  const [selectedSpace, setSelectedSpace] = useState('');
  const [chatMessage, setChatMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatStatus, setChatStatus] = useState<{ success: boolean; msg: string } | null>(null);

  // Contacts State
  const [contactLeadId, setContactLeadId] = useState('');
  const [contactLoading, setContactLoading] = useState(false);
  const [contactStatus, setContactStatus] = useState<{ success: boolean; msg: string } | null>(null);
  const [googleContacts, setGoogleContacts] = useState<GoogleContact[]>([]);
  const [googleContactsLoading, setGoogleContactsLoading] = useState(false);

  // Reset statuses on tab switch
  useEffect(() => {
    setGmailStatus(null);
    setSheetStatus(null);
    setCalStatus(null);
    setChatStatus(null);
    setContactStatus(null);
    setSyncMsg(null);
  }, [activeSubTab]);

  const handleLoadDriveFiles = async () => {
    if (!accessToken) return;
    setDriveLoading(true);
    setDriveError(null);
    try {
      const files = await listDriveFiles(accessToken, 25);
      setDriveFiles(files);
    } catch (e: any) {
      setDriveError(e.message || 'Failed to list Google Drive files');
    } finally {
      setDriveLoading(false);
    }
  };

  const handleLoadChatSpaces = async () => {
    if (!accessToken) return;
    setChatLoading(true);
    try {
      const spaces = await listChatSpaces(accessToken);
      setChatSpaces(spaces);
      if (spaces.length > 0) {
        setSelectedSpace(spaces[0].name);
      }
    } catch (e: any) {
      console.error(e);
    } finally {
      setChatLoading(false);
    }
  };

  const handleLoadInbox = async () => {
    if (!accessToken) return;
    setInboxLoading(true);
    try {
      setInboxMessages(await listRecentGmailMessages(accessToken, 8));
    } catch (e: any) {
      console.error('Inbox load failed:', e);
    } finally {
      setInboxLoading(false);
    }
  };

  const handleLoadCalEvents = async () => {
    if (!accessToken) return;
    setCalEventsLoading(true);
    try {
      setCalEvents(await listUpcomingCalendarEvents(accessToken, 8));
    } catch (e: any) {
      console.error('Calendar events load failed:', e);
    } finally {
      setCalEventsLoading(false);
    }
  };

  const handleLoadSpreadsheets = async () => {
    if (!accessToken) return;
    setSpreadsheetsLoading(true);
    try {
      setSpreadsheets(await listSpreadsheets(accessToken, 10));
    } catch (e: any) {
      console.error('Spreadsheets load failed:', e);
    } finally {
      setSpreadsheetsLoading(false);
    }
  };

  const handleLoadGoogleContacts = async () => {
    if (!accessToken) return;
    setGoogleContactsLoading(true);
    try {
      setGoogleContacts(await listGoogleContacts(accessToken, 15));
    } catch (e: any) {
      console.error('Contacts load failed:', e);
    } finally {
      setGoogleContactsLoading(false);
    }
  };

  const loadTracked = async () => {
    setTrackedEmails(await fetchTrackedEmails());
  };

  // Load all live workspace data once connected
  useEffect(() => {
    if (accessToken) {
      handleLoadDriveFiles();
      handleLoadChatSpaces();
      handleLoadInbox();
      handleLoadCalEvents();
      handleLoadSpreadsheets();
      handleLoadGoogleContacts();
      loadTracked();
    }
  }, [accessToken]);

  // Handle Gmail Send — every outbound email carries a unique tracking reference
  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken || isGoogleTokenExpired()) {
      notifyGoogleTokenExpired('Google access token expired before sending email.');
      setGmailStatus({ success: false, msg: 'Google session expired. Please re-authenticate to send email.' });
      return;
    }
    if (!gmailTo || !gmailSubject || !gmailBody) {
      setGmailStatus({ success: false, msg: 'Please complete all email fields' });
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to send this email via Gmail to ${gmailTo}?`);
    if (!confirmed) return;

    setGmailLoading(true);
    setGmailStatus(null);
    try {
      const trackingId = generateTrackingId();
      let finalBody = gmailBody;
      let isHtml = false;
      if (userSignature && userSignature.trim()) {
        isHtml = true;
        const bodyHtml = gmailBody.replace(/\n/g, '<br />');
        finalBody = `${bodyHtml}<br /><br />${userSignature}`;
      }
      const sent = await sendGmailEmail(accessToken, gmailTo, gmailSubject, finalBody, trackingId, isHtml);

      // Match the lead: explicit selection wins, otherwise match by recipient email
      const lead = leads.find(l => l.id === gmailLeadId)
        || leads.find(l => l.email && l.email.toLowerCase() === gmailTo.toLowerCase());

      await saveTrackedEmail({
        trackingId,
        leadId: lead?.id || null,
        leadName: lead ? `${lead.name} (${lead.company})` : null,
        threadId: sent.threadId,
        messageId: sent.id,
        recipient: gmailTo,
        subject: gmailSubject,
        sentBy: currentUserEmail,
        sentAt: new Date().toISOString(),
        replyCount: 0,
      });
      await loadTracked();

      setGmailStatus({ success: true, msg: `Email sent with tracking reference [#${trackingId}]${lead ? ` — linked to ${lead.name}` : ''}. Replies will attach automatically on sync.` });
      setGmailBody('');
    } catch (err: any) {
      setGmailStatus({ success: false, msg: err.message || 'Failed to send email.' });
    } finally {
      setGmailLoading(false);
    }
  };

  // Check tracked Gmail threads for new replies and attach them to their leads
  const handleSyncReplies = async () => {
    if (!accessToken) return;
    setSyncLoading(true);
    setSyncMsg(null);
    let newReplies = 0;
    try {
      for (const t of trackedEmails) {
        if (!t.threadId) continue;
        let msgs: GmailMessageMeta[] = [];
        try {
          msgs = await fetchGmailThreadMessages(accessToken, t.threadId);
        } catch {
          continue; // thread may have been deleted
        }
        const replies = msgs.filter(m =>
          m.id !== t.messageId &&
          !(currentUserEmail && m.from.toLowerCase().includes(currentUserEmail.toLowerCase()))
        );
        const known = t.replyCount || 0;
        if (replies.length > known) {
          const fresh = replies.slice(known);
          const last = fresh[fresh.length - 1];
          await saveTrackedEmail({
            ...t,
            replyCount: replies.length,
            lastReplyAt: last.date,
            lastReplyFrom: last.from,
            lastReplySnippet: last.snippet,
          });
          if (t.leadId && onLeadReply) {
            for (const r of fresh) {
              onLeadReply(t.leadId, { from: r.from, date: r.date, snippet: r.snippet, trackingId: t.trackingId });
            }
          }
          newReplies += fresh.length;
        }
      }
      await loadTracked();
      setSyncMsg(newReplies > 0
        ? `Found ${newReplies} new repl${newReplies === 1 ? 'y' : 'ies'} — attached to the linked lead${newReplies === 1 ? '' : 's'}.`
        : 'No new replies found. All tracked conversations are up to date.');
    } catch (err: any) {
      setSyncMsg(`Sync failed: ${err.message}`);
    } finally {
      setSyncLoading(false);
    }
  };

  // Handle Sheets Sync
  const handleSyncToSheets = async () => {
    if (!accessToken) return;

    const confirmed = window.confirm('This will create a new Spreadsheet in your Google Drive and export all CRM Leads. Continue?');
    if (!confirmed) return;

    setSheetLoading(true);
    setSheetStatus(null);
    try {
      const title = `Armellini CRM Leads Export - ${new Date().toLocaleDateString()}`;
      const sheetData = await createGoogleSheet(accessToken, title);
      const spreadsheetId = sheetData.spreadsheetId;
      const spreadsheetUrl = sheetData.spreadsheetUrl;

      const headers = ['ID', 'Lead Name', 'Company', 'Email', 'Phone', 'Owner', 'Service', 'Industry', 'Deal Value ($)', 'Pipeline Status', 'Location'];
      const rows = leads.map(l => [
        l.id,
        l.name,
        l.company,
        l.email,
        l.phone,
        l.owner || 'Unassigned',
        l.service || 'None',
        l.industry || 'None',
        l.dealValue,
        l.status,
        l.location || 'N/A'
      ]);

      await appendToGoogleSheet(accessToken, spreadsheetId, 'Sheet1!A1', [headers, ...rows]);

      setSheetStatus({
        success: true,
        msg: `Successfully synced ${leads.length} leads to your Google Drive!`,
        url: spreadsheetUrl
      });
      handleLoadSpreadsheets();
    } catch (err: any) {
      setSheetStatus({ success: false, msg: err.message || 'Failed to export leads.' });
    } finally {
      setSheetLoading(false);
    }
  };

  // Handle Calendar Scheduling
  const handleScheduleEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken) return;
    if (!calSummary || !calDate || !calTime) {
      setCalStatus({ success: false, msg: 'Please enter Summary, Date, and Time' });
      return;
    }

    const confirmed = window.confirm(`Confirm scheduling "${calSummary}" in your Google Calendar?`);
    if (!confirmed) return;

    setCalLoading(true);
    setCalStatus(null);
    try {
      const startIso = new Date(`${calDate}T${calTime}:00`).toISOString();
      const endIso = new Date(new Date(`${calDate}T${calTime}:00`).getTime() + 60 * 60 * 1000).toISOString();

      await createCalendarEvent(accessToken, calSummary, calDescription, startIso, endIso);
      setCalStatus({ success: true, msg: 'Successfully scheduled Google Calendar event!' });
      setCalSummary('');
      handleLoadCalEvents();
    } catch (err: any) {
      setCalStatus({ success: false, msg: err.message || 'Failed to schedule event.' });
    } finally {
      setCalLoading(false);
    }
  };

  // Handle Contact Sync
  const handleSyncContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken || !contactLeadId) return;

    const lead = leads.find(l => l.id === contactLeadId);
    if (!lead) return;

    const confirmed = window.confirm(`Do you want to add ${lead.name} from ${lead.company} to your Google Contacts?`);
    if (!confirmed) return;

    setContactLoading(true);
    setContactStatus(null);
    try {
      const nameParts = lead.name.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      await createGoogleContact(accessToken, firstName, lastName, lead.company, lead.email, lead.phone);
      setContactStatus({ success: true, msg: `Synced contact ${lead.name} to Google Contacts successfully!` });
      handleLoadGoogleContacts();
    } catch (err: any) {
      setContactStatus({ success: false, msg: err.message || 'Failed to sync contact.' });
    } finally {
      setContactLoading(false);
    }
  };

  // Handle Google Chat Message
  const handlePostChatMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken) return;
    if (!selectedSpace || !chatMessage) {
      setChatStatus({ success: false, msg: 'Please select a Space and enter a message.' });
      return;
    }

    const confirmed = window.confirm('Post this update to the selected Google Chat Space?');
    if (!confirmed) return;

    setChatLoading(true);
    setChatStatus(null);
    try {
      await sendChatMessage(accessToken, selectedSpace, chatMessage);
      setChatStatus({ success: true, msg: 'Posted message to Google Chat Space successfully!' });
      setChatMessage('');
    } catch (err: any) {
      setChatStatus({ success: false, msg: err.message || 'Failed to post message.' });
    } finally {
      setChatLoading(false);
    }
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-red-500/10 text-red-600 border-red-500/20 dark:text-red-400',
      'bg-blue-500/10 text-blue-600 border-blue-500/20 dark:text-blue-400',
      'bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400',
      'bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400',
      'bg-purple-500/10 text-purple-600 border-purple-500/20 dark:text-purple-400',
      'bg-indigo-500/10 text-indigo-600 border-indigo-500/20 dark:text-indigo-400',
      'bg-teal-500/10 text-teal-600 border-teal-500/20 dark:text-teal-400',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const modules: { key: typeof activeSubTab; icon: React.ReactNode; label: string; activeColor: string; bgHover: string }[] = [
    { key: 'gmail', icon: <Mail className="w-4 h-4" />, label: 'Gmail Campaigns', activeColor: 'bg-red-600 text-white shadow-red-500/10', bgHover: 'hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/20 dark:hover:text-red-400' },
    { key: 'sheets', icon: <FileSpreadsheet className="w-4 h-4" />, label: 'Google Sheets Sync', activeColor: 'bg-emerald-600 text-white shadow-emerald-500/10', bgHover: 'hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-emerald-950/20 dark:hover:text-emerald-400' },
    { key: 'calendar', icon: <Calendar className="w-4 h-4" />, label: 'Google Calendar', activeColor: 'bg-blue-600 text-white shadow-blue-500/10', bgHover: 'hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-950/20 dark:hover:text-blue-400' },
    { key: 'drive', icon: <FolderOpen className="w-4 h-4" />, label: 'Drive File Hub', activeColor: 'bg-amber-600 text-white shadow-amber-500/10', bgHover: 'hover:bg-amber-50 hover:text-amber-600 dark:hover:bg-amber-950/20 dark:hover:text-amber-400' },
    { key: 'chat', icon: <MessageSquare className="w-4 h-4" />, label: 'Google Chat Alerts', activeColor: 'bg-teal-600 text-white shadow-teal-500/10', bgHover: 'hover:bg-teal-50 hover:text-teal-600 dark:hover:bg-teal-950/20 dark:hover:text-teal-400' },
    { key: 'contacts', icon: <Users className="w-4 h-4" />, label: 'Contacts Sync', activeColor: 'bg-indigo-600 text-white shadow-indigo-500/10', bgHover: 'hover:bg-indigo-50 hover:text-indigo-600 dark:hover:bg-indigo-950/20 dark:hover:text-indigo-400' },
  ];

  const statusBanner = (status: { success: boolean; msg: string } | null) => status && (
    <div className={`p-3.5 rounded-xl text-xs flex items-start gap-2.5 border transition-all ${status.success ? 'bg-emerald-50/60 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/40' : 'bg-red-50/60 dark:bg-red-950/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-900/40'}`}>
      {status.success ? <CheckCircle className="w-4 h-4 shrink-0 text-emerald-600" /> : <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />}
      <span className="font-semibold leading-normal">{status.msg}</span>
    </div>
  );

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-slate-50 dark:bg-slate-900 transition-colors">
      
      {/* Decorative Gradient Background Elements */}
      <div className="absolute right-0 top-0 w-96 h-96 bg-blue-500/5 dark:bg-blue-500/5 blur-3xl pointer-events-none rounded-full" />
      <div className="absolute left-1/3 bottom-10 w-96 h-96 bg-indigo-500/5 dark:bg-indigo-500/5 blur-3xl pointer-events-none rounded-full" />

      {/* Header Info */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-5 relative z-10">
        <div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight flex items-center gap-2 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 dark:from-white dark:via-blue-200 dark:to-indigo-300 bg-clip-text text-transparent">
            <span className="inline-flex p-1.5 bg-blue-500/10 rounded-xl">
              <Globe className="w-6 h-6 text-blue-600 animate-spin-slow" />
            </span>
            Google Workspace Integration
          </h1>
          <p className="text-xs md:text-sm font-semibold text-slate-500 dark:text-slate-400 mt-1">
            Live corporate Workspace connection — campaigns, Sheets databases, Calendar sync, and automatic email replies routing.
          </p>
        </div>

        <div>
          {accessToken ? (
            <div className="flex items-center gap-3 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-2.5 rounded-xl shadow-sm">
              <div className="relative flex items-center justify-center">
                <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </div>
              <div className="text-left">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Status</p>
                <p className="text-xs font-bold text-slate-800 dark:text-slate-200 mt-0.5">{currentUserEmail || 'Connected'}</p>
              </div>
              <button
                onClick={onDisconnectGoogle}
                className="text-[11px] bg-slate-100 dark:bg-slate-900 hover:bg-red-50 dark:hover:bg-red-950/40 text-slate-600 dark:text-slate-300 hover:text-red-600 border border-slate-200/60 dark:border-slate-800 px-3 py-1.5 rounded-lg font-black transition cursor-pointer"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <button
              onClick={onConnectGoogle}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded-xl text-xs transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.02] cursor-pointer"
            >
              <Globe className="w-4 h-4" /> Connect Google Workspace
            </button>
          )}
        </div>
      </div>

      {!accessToken ? (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-white via-white to-slate-50 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900/60 border border-slate-200 dark:border-slate-800/80 rounded-2xl p-8 max-w-2xl mx-auto text-center mt-12 shadow-xl relative overflow-hidden"
        >
          <div className="absolute right-0 top-0 w-64 h-64 bg-blue-500/5 blur-2xl rounded-full" />
          <Globe className="w-16 h-16 text-blue-500/20 dark:text-blue-400/20 mx-auto mb-4 animate-pulse" />
          <h3 className="text-lg font-black text-slate-800 dark:text-slate-100 flex items-center justify-center gap-2">
            <Lock className="w-4 h-4 text-blue-500" /> Workspace Authorization Required
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2.5 max-w-md mx-auto leading-relaxed">
            Drive smart email outreach, synchronise lead pipelines directly to Google Sheets, schedule calendar follow-ups, sync contacts, and monitor spaces.
          </p>

          <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto mt-6 text-left">
            <div className="flex items-center gap-2 p-2 bg-slate-100/50 dark:bg-slate-900/50 rounded-lg border border-slate-200/50 dark:border-slate-800/50">
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
              <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300">Secure OAuth2</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-slate-100/50 dark:bg-slate-900/50 rounded-lg border border-slate-200/50 dark:border-slate-800/50">
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
              <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300">Direct Google APIs</span>
            </div>
          </div>

          <button
            onClick={onConnectGoogle}
            className="mt-8 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-extrabold px-6 py-3 rounded-xl text-xs transition shadow-lg shadow-blue-500/20 hover:shadow-blue-500/35 hover:scale-[1.02] cursor-pointer"
          >
            Connect Workspace Account <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start relative z-10">

          {/* Sub Navigation */}
          <div className="lg:col-span-1 bg-white dark:bg-slate-950 rounded-2xl p-3 border border-slate-200 dark:border-slate-800/80 flex flex-col gap-1.5 lg:sticky lg:top-4 shadow-sm">
            <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 font-mono tracking-widest uppercase mb-2 px-2.5">Modules</span>
            {modules.map(m => {
              const isActive = activeSubTab === m.key;
              return (
                <button
                  key={m.key}
                  onClick={() => setActiveSubTab(m.key)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-bold text-left transition-all cursor-pointer ${
                    isActive
                      ? `${m.activeColor} shadow-md`
                      : `text-slate-600 dark:text-slate-300 ${m.bgHover}`
                  }`}
                >
                  {m.icon} {m.label}
                </button>
              );
            })}

            {/* Email Tracking Health Widget */}
            <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-4 px-2 text-left">
              <p className="text-[10px] font-black text-slate-450 dark:text-slate-500 font-mono tracking-widest uppercase mb-2">Tracking Health</p>
              <div className="bg-slate-50 dark:bg-slate-900/60 rounded-xl p-3 border border-slate-200/50 dark:border-slate-800/50">
                <div className="flex justify-between items-baseline">
                  <span className="text-2xl font-black text-slate-800 dark:text-white">{trackedEmails.length}</span>
                  <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 px-1.5 py-0.5 rounded">Campaigns</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1 leading-normal font-semibold">
                  {trackedEmails.reduce((a, t) => a + (t.replyCount || 0), 0)} client replies linked to pipeline cards.
                </p>
              </div>
            </div>
          </div>

          {/* Tab Workspaces */}
          <div className="lg:col-span-4 min-h-[500px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSubTab}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.15 }}
              >
                
                {/* 1. GMAIL INTEGRATION */}
                {activeSubTab === 'gmail' && (
                  <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
                    <form onSubmit={handleSendEmail} className={`${panelCls} xl:col-span-3 space-y-4`}>
                      <h3 className="text-base font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                        <Mail className="w-5 h-5 text-red-500" /> Tracked Gmail Composer
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 leading-normal font-medium">
                        Send outbound follow-ups directly through the Gmail API. Replies are automatically attached to the selected lead.
                      </p>

                      <div>
                        <label className={labelCls}>
                          Select CRM Lead Contact
                          <Tooltip text="Automatically pre-fills the recipient email, subject line, and a personalized template using the selected CRM lead contact details." />
                        </label>
                        <select
                          value={gmailLeadId}
                          onChange={(e) => {
                            setGmailLeadId(e.target.value);
                            const lead = leads.find(l => l.id === e.target.value);
                            if (lead) {
                              setGmailTo(lead.email);
                              setGmailSubject(`Armellini Cold-chain logistics outreach: ${lead.company}`);
                              setGmailBody(`Hi ${lead.name},\n\nHope this finds you well. I'm following up regarding your freight shipping needs for ${lead.company}.\n\nLet's schedule a call to review quotes.\n\nBest regards,\n${auth.currentUser?.displayName || 'Mark Sterling'}`);
                            }
                          }}
                          className={`${inputCls} cursor-pointer`}
                        >
                          <option value="">-- Choose Lead to Pre-fill --</option>
                          {leads.filter(l => l.email).map(l => (
                            <option key={l.id} value={l.id}>{l.name} ({l.company})</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className={labelCls}>
                          Recipient Email
                          <Tooltip text="The destination email address. Pre-fills automatically if you select a CRM lead above." />
                        </label>
                        <input
                          type="email"
                          value={gmailTo}
                          onChange={(e) => setGmailTo(e.target.value)}
                          placeholder="client@company.com"
                          className={inputCls}
                        />
                      </div>

                      <div>
                        <label className={labelCls}>
                          Subject Title
                          <Tooltip text="The title of your email. We automatically insert an invisible, unique tracking reference token inside the subject so replies link back to this lead." />
                        </label>
                        <input
                          type="text"
                          value={gmailSubject}
                          onChange={(e) => setGmailSubject(e.target.value)}
                          placeholder="Logistics Rate Negotiation"
                          className={inputCls}
                        />
                      </div>

                      <div>
                        <label className={labelCls}>
                          Email Body Content
                          <Tooltip text="The main body text of your outreach message. Support normal text styling and will show in the recipient's inbox under your corporate Gmail address." />
                        </label>
                        <textarea
                          rows={6}
                          value={gmailBody}
                          onChange={(e) => setGmailBody(e.target.value)}
                          placeholder="Type your email body here..."
                          className={`${inputCls} font-sans`}
                        />
                        {userSignature && (
                          <div className="mt-2 p-3 bg-slate-50 dark:bg-slate-900/30 rounded-xl border border-slate-200/50 dark:border-slate-800 text-xs">
                            <span className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                              Signature (will be appended automatically)
                            </span>
                            <div className="max-h-32 overflow-y-auto scale-90 origin-top-left">
                              <div dangerouslySetInnerHTML={{ __html: userSignature }} />
                            </div>
                          </div>
                        )}
                      </div>

                      {statusBanner(gmailStatus)}

                      <button
                        type="submit"
                        disabled={gmailLoading}
                        className="w-full bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-extrabold text-xs px-4 py-2.5 rounded-xl transition shadow-md shadow-red-500/10 hover:shadow-red-500/20 active:scale-[0.99] flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-55"
                      >
                        {gmailLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        {gmailLoading ? 'Sending Tracked Email...' : 'Send Tracked Email via Gmail'}
                      </button>
                    </form>

                    <div className="xl:col-span-2 space-y-6">
                      {/* Tracked Threads list */}
                      <div className={panelCls}>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                            <Reply className="w-4 h-4 text-emerald-500" /> Active Tracking Threads
                          </h4>
                          <button
                            onClick={handleSyncReplies}
                            disabled={syncLoading || trackedEmails.length === 0}
                            className="text-[11px] bg-slate-100 hover:bg-slate-200 dark:bg-slate-900 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-350 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-800 font-extrabold transition cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                          >
                            {syncLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            Sync Replies
                          </button>
                        </div>
                        {syncMsg && (
                          <div className="text-[10px] mb-3 text-slate-700 dark:text-slate-300 bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900/40 rounded-lg p-2 font-semibold flex items-center gap-1.5">
                            <AlertCircle className="w-3.5 h-3.5 text-blue-500" /> {syncMsg}
                          </div>
                        )}
                        {trackedEmails.length === 0 ? (
                          <div className="py-12 text-center">
                            <Mail className="w-8 h-8 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                            <p className="text-xs text-slate-400 font-semibold">No tracked outreach yet</p>
                            <p className="text-[10px] text-slate-400 mt-0.5">Tracked emails will show replies here.</p>
                          </div>
                        ) : (
                          <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
                            {trackedEmails.map(t => (
                              <div key={t.trackingId} className="border border-slate-100 dark:border-slate-800 rounded-xl p-3 bg-slate-50/50 dark:bg-slate-900/40 hover:border-slate-200 dark:hover:border-slate-800 transition">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-xs font-black text-slate-800 dark:text-slate-200 truncate">{t.leadName || t.recipient}</span>
                                  <span className={`text-[9px] font-mono font-black px-2 py-0.5 rounded-full shrink-0 ${(t.replyCount || 0) > 0 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-slate-250 text-slate-500 dark:bg-slate-800'}`}>
                                    {(t.replyCount || 0)} {(t.replyCount || 0) === 1 ? 'REPLY' : 'REPLIES'}
                                  </span>
                                </div>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-1" title={t.subject || ''}>
                                  {t.subject} <span className="font-mono text-[9px] text-blue-500 font-bold">[#{t.trackingId}]</span>
                                </p>
                                {t.lastReplySnippet && (
                                  <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-2 border-l-2 border-emerald-500 pl-2 italic line-clamp-2">
                                    <span className="font-bold text-slate-700 dark:text-slate-300 block not-italic mb-0.5 text-[9px]">From: {t.lastReplyFrom}</span>
                                    "{t.lastReplySnippet}"
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Recent inbox */}
                      <div className={panelCls}>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                            <Inbox className="w-4 h-4 text-blue-500" /> Recent Inbox
                          </h4>
                          <button
                            onClick={handleLoadInbox}
                            className="text-[11px] text-slate-500 hover:text-blue-600 font-black flex items-center gap-1 cursor-pointer"
                          >
                            {inboxLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} REFRESH
                          </button>
                        </div>
                        {inboxLoading && inboxMessages.length === 0 ? (
                          <div className="py-8 text-center text-xs text-slate-400 font-medium">Loading messages…</div>
                        ) : inboxMessages.length === 0 ? (
                          <p className="text-xs text-slate-400 py-8 text-center font-medium">No recent messages found.</p>
                        ) : (
                          <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                            {inboxMessages.map(m => (
                              <div key={m.id} className="text-[11px] border-b border-slate-100 dark:border-slate-800/60 pb-2 hover:bg-slate-50/30 dark:hover:bg-slate-900/10 px-1 rounded transition">
                                <div className="flex justify-between gap-2">
                                  <span className="font-bold text-slate-700 dark:text-slate-300 truncate">{m.from.replace(/<.*>/, '').trim()}</span>
                                  <span className="text-slate-400 shrink-0 text-[10px] font-mono">{m.date ? new Date(m.date).toLocaleDateString() : ''}</span>
                                </div>
                                <p className="text-slate-500 dark:text-slate-400 truncate mt-0.5 font-medium">{m.subject}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* 2. GOOGLE SHEETS */}
                {activeSubTab === 'sheets' && (
                  <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
                    <div className={`${panelCls} xl:col-span-3 space-y-5 text-left`}>
                      <h3 className="text-base font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                        <FileSpreadsheet className="w-5 h-5 text-emerald-500" /> Google Sheets Lead Sync
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                        Export your full active CRM lead pipeline into a dynamic spreadsheet inside your Google Drive. Columns are neatly formatted to help you run custom reports, sorting, or data modeling.
                      </p>

                      {/* Visual sync graphic */}
                      <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200/55 dark:border-slate-800/80 p-5 flex items-center justify-between relative overflow-hidden shadow-inner">
                        <div className="text-left z-10">
                          <p className="text-[10px] font-bold text-blue-500 uppercase tracking-wider font-mono">Source</p>
                          <p className="text-xs font-black text-slate-800 dark:text-slate-200 mt-1">Armellini CRM Data</p>
                          <p className="text-[10px] text-slate-450 dark:text-slate-500 font-semibold">{leads.length} Records ready</p>
                        </div>
                        <div className="flex flex-col items-center justify-center shrink-0 mx-4 z-10">
                          {sheetLoading ? (
                            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                          ) : (
                            <div className="flex items-center gap-1 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 px-3 py-1.5 rounded-full shadow-sm text-[10px] font-black text-slate-600 dark:text-slate-350">
                              SYNC PIPELINE <ChevronRight className="w-3.5 h-3.5 text-blue-500 animate-pulse" />
                            </div>
                          )}
                        </div>
                        <div className="text-right z-10">
                          <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider font-mono">Target</p>
                          <p className="text-xs font-black text-slate-800 dark:text-slate-200 mt-1">Google Sheet File</p>
                          <p className="text-[10px] text-slate-450 dark:text-slate-500 font-semibold">Drive Root Folder</p>
                        </div>
                      </div>

                      <button
                        onClick={handleSyncToSheets}
                        disabled={sheetLoading}
                        className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-extrabold text-xs px-4 py-3 rounded-xl transition shadow-md shadow-emerald-500/10 hover:shadow-emerald-500/20 active:scale-[0.99] flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-55"
                      >
                        {sheetLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                        {sheetLoading ? 'Creating Spreadsheet & Syncing...' : 'Export Leads to Google Sheets'}
                      </button>

                      {sheetStatus && (
                        <div className={`p-4 rounded-xl text-xs flex flex-col gap-2.5 border transition-all ${sheetStatus.success ? 'bg-emerald-50/60 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/40' : 'bg-red-50/60 dark:bg-red-950/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-900/40'}`}>
                          <div className="flex items-center gap-2">
                            {sheetStatus.success ? <CheckCircle className="w-4.5 h-4.5 text-emerald-600" /> : <AlertCircle className="w-4.5 h-4.5 text-red-500" />}
                            <span className="font-bold leading-normal">{sheetStatus.msg}</span>
                          </div>
                          {sheetStatus.url && (
                            <a
                              href={sheetStatus.url}
                              target="_blank"
                              referrerPolicy="no-referrer"
                              rel="noopener noreferrer"
                              className="mt-1.5 inline-flex items-center gap-1.5 bg-white dark:bg-slate-900 text-blue-600 dark:text-blue-400 border border-slate-200 dark:border-slate-800 hover:border-blue-300 dark:hover:border-blue-800 px-3.5 py-2 rounded-lg font-extrabold text-[11px] hover:text-blue-700 transition w-fit shadow-sm"
                            >
                              <ExternalLink className="w-3.5 h-3.5" /> Open Spreadsheet on Google Drive
                            </a>
                          )}
                        </div>
                      )}
                    </div>

                    <div className={`${panelCls} xl:col-span-2 text-left`}>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Synced Files History
                        </h4>
                        <button onClick={handleLoadSpreadsheets} className="text-[11px] text-slate-500 hover:text-blue-600 font-bold flex items-center gap-1 cursor-pointer">
                          {spreadsheetsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} REFRESH
                        </button>
                      </div>
                      {spreadsheets.length === 0 ? (
                        <div className="py-12 text-center text-xs text-slate-400 font-medium">
                          {spreadsheetsLoading ? 'Loading spreadhseets…' : 'No synced spreadsheets detected.'}
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                          {spreadsheets.map(f => (
                            <a
                              key={f.id}
                              href={f.webViewLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              referrerPolicy="no-referrer"
                              className="flex items-center justify-between gap-2.5 text-xs p-2.5 rounded-xl bg-slate-50/50 dark:bg-slate-900/40 hover:bg-slate-100 dark:hover:bg-slate-900 border border-slate-100 dark:border-slate-800/80 transition-all text-slate-700 dark:text-slate-350"
                            >
                              <span className="truncate font-semibold text-slate-800 dark:text-slate-200">{f.name}</span>
                              <ExternalLink className="w-3.5 h-3.5 shrink-0 text-blue-500 hover:scale-110 transition-transform" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 3. GOOGLE CALENDAR */}
                {activeSubTab === 'calendar' && (
                  <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
                    <form onSubmit={handleScheduleEvent} className={`${panelCls} xl:col-span-3 space-y-4 text-left`}>
                      <h3 className="text-base font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-blue-500" /> Schedule CRM Events & Follow-ups
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-normal">
                        Create follow-up phone calls or client meetings directly on your corporate Google Calendar to manage outreach tasks cleanly.
                      </p>

                      <div>
                        <label className={labelCls}>
                          Select CRM Lead Reference
                          <Tooltip text="Select a CRM lead to auto-generate a follow-up meeting title and link details back to this lead." />
                        </label>
                        <select
                          value={calLeadId}
                          onChange={(e) => {
                            const id = e.target.value;
                            setCalLeadId(id);
                            const lead = leads.find(l => l.id === id);
                            if (lead) {
                              setCalSummary(`Follow up with ${lead.name} (${lead.company})`);
                            }
                          }}
                          className={`${inputCls} cursor-pointer`}
                        >
                          <option value="">-- Choose Lead Reference --</option>
                          {leads.map(l => (
                            <option key={l.id} value={l.id}>{l.name} ({l.company})</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className={labelCls}>
                          Calendar Event Title
                          <Tooltip text="The meeting title that will show up on your Google Calendar event." />
                        </label>
                        <input
                          type="text"
                          value={calSummary}
                          onChange={(e) => setCalSummary(e.target.value)}
                          placeholder="Follow-up call"
                          className={inputCls}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className={labelCls}>
                            Event Date
                            <Tooltip text="The date the follow-up meeting or call will take place." />
                          </label>
                          <input
                            type="date"
                            value={calDate}
                            onChange={(e) => setCalDate(e.target.value)}
                            className={inputCls}
                          />
                        </div>
                        <div>
                          <label className={labelCls}>
                            Start Time (1 Hr)
                            <Tooltip text="The meeting start time. Events default to a duration of 1 hour." />
                          </label>
                          <input
                            type="time"
                            value={calTime}
                            onChange={(e) => setCalTime(e.target.value)}
                            className={inputCls}
                          />
                        </div>
                      </div>

                      <div>
                        <label className={labelCls}>
                          Event Description
                          <Tooltip text="Internal notes or meeting details that will be saved in the event description." />
                        </label>
                        <textarea
                          rows={3}
                          value={calDescription}
                          onChange={(e) => setCalDescription(e.target.value)}
                          className={inputCls}
                        />
                      </div>

                      {statusBanner(calStatus)}

                      <button
                        type="submit"
                        disabled={calLoading}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-extrabold text-xs px-4 py-2.5 rounded-xl transition shadow-md shadow-blue-500/10 hover:shadow-blue-500/20 active:scale-[0.99] flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-55"
                      >
                        {calLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-3.5 h-3.5" />}
                        {calLoading ? 'Scheduling Event...' : 'Add to Google Calendar'}
                      </button>
                    </form>

                    <div className={`${panelCls} xl:col-span-2 text-left`}>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-blue-500" /> Scheduled Meetings Feed
                        </h4>
                        <button onClick={handleLoadCalEvents} className="text-[11px] text-slate-500 hover:text-blue-600 font-bold flex items-center gap-1 cursor-pointer">
                          {calEventsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} REFRESH
                        </button>
                      </div>
                      {calEvents.length === 0 ? (
                        <div className="py-12 text-center text-xs text-slate-400 font-medium">
                          {calEventsLoading ? 'Loading calendar…' : 'No meetings scheduled.'}
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                          {calEvents.map(ev => (
                            <a
                              key={ev.id}
                              href={ev.htmlLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              referrerPolicy="no-referrer"
                              className="block border border-slate-150 dark:border-slate-800 rounded-xl p-3 bg-slate-50/50 dark:bg-slate-900/40 hover:border-blue-300 dark:hover:border-blue-900/80 transition-all"
                            >
                              <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">{ev.summary}</p>
                              <div className="flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-400 mt-1.5 font-mono">
                                <Clock className="w-3 h-3 text-blue-500" />
                                {ev.start ? new Date(ev.start).toLocaleString([], { dateStyle: 'medium', timeStyle: ev.start.includes('T') ? 'short' : undefined }) : ''}
                              </div>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 4. GOOGLE DRIVE */}
                {activeSubTab === 'drive' && (
                  <div className={`${panelCls} text-left space-y-4`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-3">
                      <div>
                        <h3 className="text-base font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                          <FolderOpen className="w-5 h-5 text-amber-500" /> Google Drive Files Explorer
                        </h3>
                        <p className="text-xs text-slate-550 dark:text-slate-400 mt-0.5">
                          Directly verify, browse, and access quotes, spreadsheets, or shipping details stored in Google Drive.
                        </p>
                      </div>
                      <button
                        onClick={handleLoadDriveFiles}
                        disabled={driveLoading}
                        className="text-xs bg-slate-50 hover:bg-slate-100 dark:bg-slate-900 dark:hover:bg-slate-850 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-800 px-3.5 py-2 rounded-lg font-bold transition cursor-pointer flex items-center justify-center gap-1.5"
                      >
                        {driveLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        Refresh Explorer
                      </button>
                    </div>

                    {driveLoading ? (
                      <div className="py-20 text-center text-xs text-slate-500 dark:text-slate-400 flex flex-col items-center justify-center gap-2.5">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                        <span className="font-semibold">Loading Google Drive database…</span>
                      </div>
                    ) : driveError ? (
                      <div className="p-4 bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 text-xs rounded border border-red-100 dark:border-red-900/40 font-semibold">
                        {driveError}
                      </div>
                    ) : driveFiles.length === 0 ? (
                      <div className="py-16 text-center text-xs text-slate-400 font-semibold">
                        No Drive files found in the current account.
                      </div>
                    ) : (
                      <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-950/40">
                        <div className="overflow-x-auto">
                          <table className="w-full text-left border-collapse text-xs">
                            <thead>
                              <tr className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 uppercase tracking-wider font-bold text-[10px]">
                                <th className="p-3 pl-5">File Name</th>
                                <th className="p-3">Type</th>
                                <th className="p-3 text-right pr-5">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {driveFiles.map(file => (
                                <tr key={file.id} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50/50 dark:hover:bg-slate-900/30">
                                  <td className="p-3 pl-5 font-bold text-slate-800 dark:text-slate-200 truncate max-w-[280px]" title={file.name}>
                                    {file.name}
                                  </td>
                                  <td className="p-3 text-slate-500 dark:text-slate-400 font-mono text-[10px]">
                                    {file.mimeType.split('.').pop() || file.mimeType}
                                  </td>
                                  <td className="p-3 text-right pr-5">
                                    {file.webViewLink && (
                                      <a
                                        href={file.webViewLink}
                                        target="_blank"
                                        referrerPolicy="no-referrer"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 dark:text-blue-400 hover:underline font-bold inline-flex items-center gap-0.5"
                                      >
                                        Open file <ExternalLink className="w-3 h-3" />
                                      </a>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* 5. GOOGLE CHAT MODULE */}
                {activeSubTab === 'chat' && (
                  <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
                    <form onSubmit={handlePostChatMessage} className={`${panelCls} xl:col-span-3 space-y-4 text-left`}>
                      <h3 className="text-base font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-teal-500" /> Pipeline Notifications to Google Chat
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-normal">
                        Keep teams aligned by posting CRM progress updates (won deals, negotiation steps) directly to shared spaces.
                      </p>

                      <div>
                        <label className={labelCls}>
                          Select Active Space
                          <Tooltip text="Choose one of your active Google Chat rooms or spaces to post this message update to." />
                        </label>
                        {chatSpaces.length === 0 ? (
                          <div className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-800 flex justify-between items-center">
                            <span className="font-semibold">No active spaces found.</span>
                            <button
                              type="button"
                              onClick={handleLoadChatSpaces}
                              className="text-[10px] text-blue-600 underline font-bold"
                            >
                              Retry Load
                            </button>
                          </div>
                        ) : (
                          <select
                            value={selectedSpace}
                            onChange={(e) => setSelectedSpace(e.target.value)}
                            className={`${inputCls} cursor-pointer`}
                          >
                            {chatSpaces.map(space => (
                              <option key={space.name} value={space.name}>{space.displayName || space.name}</option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div>
                        <label className={labelCls}>
                          Write Space Notification
                          <Tooltip text="Compose the custom CRM alert message you want to broadcast to your team in this space." />
                        </label>
                        <textarea
                          rows={4}
                          value={chatMessage}
                          onChange={(e) => setChatMessage(e.target.value)}
                          placeholder="E.g., [ALERT] Mark Sterling has advanced Florida Rose Growers to WON/Complete! Deal value: $45,000."
                          className={inputCls}
                        />
                      </div>

                      {statusBanner(chatStatus)}

                      <button
                        type="submit"
                        disabled={chatLoading || !selectedSpace}
                        className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-extrabold text-xs px-4 py-2.5 rounded-xl transition shadow-md shadow-teal-500/10 hover:shadow-teal-500/20 active:scale-[0.99] flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-55"
                      >
                        {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
                        {chatLoading ? 'Posting Alert...' : 'Post Google Chat Message'}
                      </button>
                    </form>

                    <div className={`${panelCls} xl:col-span-2 text-left`}>
                      <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2 mb-4">
                        <MessageSquare className="w-4 h-4 text-teal-600" /> Active Workspace Spaces
                      </h4>
                      {chatSpaces.length === 0 ? (
                        <p className="text-xs text-slate-400 py-12 text-center font-medium">No shared spaces linked to this account.</p>
                      ) : (
                        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                          {chatSpaces.map(space => (
                            <div key={space.name} className="flex items-center justify-between text-xs p-2.5 rounded-xl bg-slate-50/50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800/80">
                              <span className="font-semibold text-slate-800 dark:text-slate-200 truncate">{space.displayName || space.name}</span>
                              <span className="text-[9px] font-mono font-bold text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded uppercase shrink-0">{space.type}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 6. GOOGLE CONTACTS */}
                {activeSubTab === 'contacts' && (
                  <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
                    <form onSubmit={handleSyncContact} className={`${panelCls} xl:col-span-3 space-y-4 text-left`}>
                      <h3 className="text-base font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                        <UserCheck className="w-5 h-5 text-indigo-500" /> Export CRM Leads to Google Contacts
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-normal">
                        Keep your corporate smartphone or address book fully integrated. Export CRM lead entries directly to your account's Google Contacts registry.
                      </p>

                      <div>
                        <label className={labelCls}>
                          Select CRM Lead record to Sync
                          <Tooltip text="Select an active lead contact to synchronize directly to your Google Contacts list." />
                        </label>
                        <select
                          value={contactLeadId}
                          onChange={(e) => setContactLeadId(e.target.value)}
                          className={`${inputCls} cursor-pointer`}
                        >
                          <option value="">-- Choose Lead --</option>
                          {leads.map(l => (
                            <option key={l.id} value={l.id}>{l.name} - {l.company} ({l.email || 'No email'})</option>
                          ))}
                        </select>
                      </div>

                      {statusBanner(contactStatus)}

                      <button
                        type="submit"
                        disabled={contactLoading || !contactLeadId}
                        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-extrabold text-xs px-4 py-2.5 rounded-xl transition shadow-md shadow-indigo-500/10 hover:shadow-indigo-500/20 active:scale-[0.99] flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-55"
                      >
                        {contactLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
                        {contactLoading ? 'Exporting Contact...' : 'Export to Google Contacts'}
                      </button>
                    </form>

                    <div className={`${panelCls} xl:col-span-2 text-left`}>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                          <Users className="w-4 h-4 text-indigo-600" /> Corporate Contacts
                        </h4>
                        <button onClick={handleLoadGoogleContacts} className="text-[11px] text-slate-500 hover:text-blue-600 font-bold flex items-center gap-1 cursor-pointer">
                          {googleContactsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} REFRESH
                        </button>
                      </div>
                      {googleContacts.length === 0 ? (
                        <div className="py-12 text-center text-xs text-slate-400 font-medium">
                          {googleContactsLoading ? 'Loading contacts…' : 'No corporate contacts registered.'}
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                          {googleContacts.map((c, i) => (
                            <div key={i} className="flex items-center gap-2.5 p-2 rounded-xl bg-slate-50/50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800/80">
                              <div className={`w-8 h-8 rounded-full border flex items-center justify-center text-xs font-black shrink-0 ${getAvatarColor(c.name)}`}>
                                {c.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                              </div>
                              <div className="text-left truncate min-w-0">
                                <div className="flex justify-between gap-1 items-baseline">
                                  <span className="font-bold text-slate-800 dark:text-slate-200 truncate text-xs">{c.name}</span>
                                  {c.company && <span className="text-[9px] text-slate-400 font-bold truncate max-w-[80px] shrink-0 uppercase">{c.company}</span>}
                                </div>
                                <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate mt-0.5">{[c.email, c.phone].filter(Boolean).join(' · ')}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </motion.div>
            </AnimatePresence>
          </div>

        </div>
      )}

    </div>
  );
}
